import { useEffect, useMemo, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useSelector } from "@jti/store/reduxHooks";
import { selectFreeGifts } from "@jti/store/selectors/selectFreeGifts";
import { selectProductsInCart } from "@jti/store/selectors/selectProductsInCart";

export type SelectedFreeGifts = { [parentGiftId: string]: { [giftSku: string]: number } };

export type SetSelectedFreeGiftQuantity = ({
  parentGiftId,
  giftSku,
  quantity,
}: {
  parentGiftId: string;
  giftSku: string;
  quantity: number;
}) => void;

export const useFreeGifts = (): ReturnType<typeof selectFreeGifts> & {
  selectedFreeGifts: SelectedFreeGifts;
  setSelectedFreeGiftQuantity: SetSelectedFreeGiftQuantity;
  totalNumberOfGiftsAddedToCart: number;
} => {
  const { giftsByProduct, isFreeGiftsEnabled, limit } = useSelector(selectFreeGifts);
  const { showPricesWithTaxExcluded = false } = useConfig();
  const productsInCart = useSelector((state) => selectProductsInCart(state, false, showPricesWithTaxExcluded));

  const [selectedFreeGifts, setSelectedFreeGifts] = useState<SelectedFreeGifts>({});

  useEffect(() => {
    if (productsInCart.length === 0) return;

    const initial: SelectedFreeGifts = {};

    for (const item of productsInCart) {
      if (!item?.isGift || !item?.parentGiftId) continue;

      const parentId = item.parentGiftId;
      const giftSku = item.sku;
      const quantity = item.quantity ?? 0;

      if (!initial[parentId]) {
        initial[parentId] = {};
      }

      initial[parentId][giftSku] = quantity;
    }

    setSelectedFreeGifts(initial);
  }, [productsInCart]);

  const setSelectedFreeGiftQuantity: SetSelectedFreeGiftQuantity = ({ parentGiftId, giftSku, quantity }) => {
    setSelectedFreeGifts((prev) => ({
      ...prev,
      [parentGiftId]: {
        ...prev[parentGiftId],
        [giftSku]: quantity,
      },
    }));
  };

  const totalNumberOfGiftsAddedToCart = useMemo(() => {
    return Object.values(selectedFreeGifts).reduce((acc, gifts) => {
      return acc + Object.values(gifts).reduce((sum, q) => sum + q, 0);
    }, 0);
  }, [selectedFreeGifts]);

  return {
    giftsByProduct,
    isFreeGiftsEnabled,
    limit,
    selectedFreeGifts,
    setSelectedFreeGiftQuantity,
    totalNumberOfGiftsAddedToCart,
  };
};
